import type {
  ColumnDef,
  ColumnOrderState,
  ColumnSizingState,
  VisibilityState,
} from '@tanstack/react-table';
import { MIN_COLUMN_WIDTH, isFlexColumn } from './column-helpers';

/**
 * Versioned, pure, React-free persistence module for WmsTable column layout
 * (visibility + order + sizing). Owns a single blob per `tableKey` under the
 * existing `wms-cols-${tableKey}` localStorage key (D8) — no orphaned second
 * key. v1 was a bare `VisibilityState` object; `migrateLegacy` upgrades it.
 */

export const COLUMN_PREFS_VERSION = 2 as const; // v1 = legacy bare VisibilityState

/**
 * Per-table "extra space distribution" preference (D-spaceMode): how freed
 * width (from hidden columns, or simply a container wider than the sum of
 * column widths) is distributed. Absent means "use the table's internal
 * default" — see WmsTable's `spaceMode` resolution — so this type only ever
 * carries the three explicit, user-facing choices.
 */
export type SpaceMode = 'even' | 'first' | 'last';

export interface ColumnPrefs {
  schemaVersion: number;
  columnVisibility: VisibilityState; // { [colId]: boolean }
  columnOrder: ColumnOrderState; // string[]
  columnSizing: ColumnSizingState; // { [colId]: number }
  /** Optional — additive, schemaVersion stays 2. Omitted when unset/default. */
  spaceMode?: SpaceMode;
}

export function storageKeyFor(tableKey: string): string {
  return `wms-cols-${tableKey}`;
}

export function emptyPrefs(): ColumnPrefs {
  return {
    schemaVersion: COLUMN_PREFS_VERSION,
    columnVisibility: {},
    columnOrder: [],
    columnSizing: {},
  };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function coerceVisibility(value: unknown): VisibilityState {
  if (!isPlainObject(value)) return {};
  const out: VisibilityState = {};
  for (const [id, v] of Object.entries(value)) {
    if (typeof v === 'boolean') out[id] = v;
  }
  return out;
}

function coerceColumnOrder(value: unknown): ColumnOrderState {
  if (!Array.isArray(value)) return [];
  return value.filter((id): id is string => typeof id === 'string');
}

function coerceColumnSizing(value: unknown): ColumnSizingState {
  if (!isPlainObject(value)) return {};
  const out: ColumnSizingState = {};
  for (const [id, v] of Object.entries(value)) {
    if (typeof v === 'number' && Number.isFinite(v)) out[id] = v;
  }
  return out;
}

/** Only the three explicit values are valid; anything else (including a
 * missing/undefined field) is dropped rather than defaulted here — the
 * default lives in WmsTable's `spaceMode` resolution, not in this blob. */
function coerceSpaceMode(value: unknown): SpaceMode | undefined {
  return value === 'even' || value === 'first' || value === 'last'
    ? value
    : undefined;
}

/** v1 was a bare `VisibilityState` object (`{ colId: boolean }`). Wrap it into v2. */
export function migrateLegacy(raw: unknown): ColumnPrefs {
  return {
    schemaVersion: COLUMN_PREFS_VERSION,
    columnVisibility: coerceVisibility(raw),
    columnOrder: [],
    columnSizing: {},
  };
}

/**
 * Normalize an already-parsed (non-JSON-string) value into a `ColumnPrefs`
 * blob: migrates a legacy bare `VisibilityState`, drops an unknown future
 * `schemaVersion`, and coerces each field through the tolerant `coerce*`
 * helpers. Shared by `loadColumnPrefs` (parses JSON first) and
 * `applyColumnPrefs` (receives an already-parsed value). Never throws.
 */
function normalizeColumnPrefs(parsed: unknown): ColumnPrefs {
  const schemaVersion = isPlainObject(parsed)
    ? (parsed as { schemaVersion?: unknown }).schemaVersion
    : undefined;

  if (typeof schemaVersion !== 'number' || schemaVersion < 2) {
    return migrateLegacy(parsed);
  }
  if (schemaVersion > COLUMN_PREFS_VERSION) {
    // Forward-safe drop: a future schema version this build doesn't know how
    // to read — mirrors table-state-codec's parseState fallback behavior.
    return emptyPrefs();
  }

  const obj = parsed as Record<string, unknown>;
  const spaceMode = coerceSpaceMode(obj.spaceMode);
  return {
    schemaVersion: COLUMN_PREFS_VERSION,
    columnVisibility: coerceVisibility(obj.columnVisibility),
    columnOrder: coerceColumnOrder(obj.columnOrder),
    columnSizing: coerceColumnSizing(obj.columnSizing),
    ...(spaceMode ? { spaceMode } : {}),
  };
}

/** Read + migrate the persisted blob for `tableKey`. Never throws. */
export function loadColumnPrefs(tableKey: string): ColumnPrefs {
  let raw: string | null;
  try {
    raw = localStorage.getItem(storageKeyFor(tableKey));
  } catch {
    return emptyPrefs();
  }
  if (raw == null) return emptyPrefs();

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return emptyPrefs();
  }

  return normalizeColumnPrefs(parsed);
}

/**
 * Snapshot the currently-persisted column layout for `tableKey` (D-Saved
 * Views: captured into a saved view's `columns` bag). Before reading, fires a
 * `wms-column-prefs-flush` event so any mounted `WmsTable` for this key can
 * synchronously flush a pending debounced save first — DOM event dispatch is
 * synchronous, so listeners complete before `dispatchEvent` returns, meaning
 * the subsequent read below always sees the flushed write. Mirrors the
 * `wms-column-prefs-applied` event `applyColumnPrefs` dispatches. After the
 * flush, reuses the same tolerant read path as `loadColumnPrefs`. Never
 * throws.
 */
export function snapshotColumnPrefs(tableKey: string): ColumnPrefs {
  try {
    window.dispatchEvent(
      new CustomEvent('wms-column-prefs-flush', { detail: { tableKey } }),
    );
  } catch {
    // ignore — matches the existing WmsTable persistence try/catch pattern
  }
  return loadColumnPrefs(tableKey);
}

/**
 * Apply a saved view's column layout for `tableKey`: normalize `raw` through
 * the same tolerant pipeline as `loadColumnPrefs` (accepts a v2 blob shape;
 * malformed input or a future `schemaVersion` falls back to `emptyPrefs`),
 * persist it under the standard `wms-cols-${tableKey}` key, then notify any
 * mounted `WmsTable` for this key via a `wms-column-prefs-applied` custom
 * event so it can reconcile + adopt the new layout live. Never throws.
 */
export function applyColumnPrefs(tableKey: string, raw: unknown): void {
  const prefs = normalizeColumnPrefs(raw);
  saveColumnPrefs(tableKey, prefs);
  try {
    window.dispatchEvent(
      new CustomEvent('wms-column-prefs-applied', { detail: { tableKey } }),
    );
  } catch {
    // ignore — matches the existing WmsTable persistence try/catch pattern
  }
}

/** Persist the blob for `tableKey`. Never throws. */
export function saveColumnPrefs(tableKey: string, prefs: ColumnPrefs): void {
  try {
    localStorage.setItem(storageKeyFor(tableKey), JSON.stringify(prefs));
  } catch {
    // ignore — matches the existing WmsTable persistence try/catch pattern
  }
}

export function columnIdOf(col: ColumnDef<any, any>): string | undefined {
  // Mirror TanStack's own id resolution: an explicit `id` wins over
  // `accessorKey` (TanStack resolves `id ?? accessorKey`). Columns that
  // declare both — e.g. { id: 'customer', accessorKey: 'customerName' } —
  // are keyed by their `id` in every table-state map, so reconcilePrefs must
  // key on `id` too or it drops their saved visibility/order/sizing as
  // "unknown".
  if ('id' in col && typeof (col as any).id === 'string') {
    return (col as any).id;
  }
  if ('accessorKey' in col && typeof (col as any).accessorKey === 'string') {
    return (col as any).accessorKey;
  }
  return undefined;
}

/**
 * Reconcile a loaded/migrated blob against the table's current column defs.
 * Prefers reconciling stale state over wiping it. Rules R1–R6 (see plan D8):
 *  - R1: drop unknown visibility ids.
 *  - R2: strip `enableHiding:false` columns from visibility (prevents stale
 *    saved state from hiding a newly-required/structural column).
 *  - R3: sanitize order — existing ids only, deduped (keep first); does NOT
 *    inject new ids (TanStack appends absent ids in definition order itself).
 *  - R4: drop unknown sizing ids.
 *  - R5: drop sizing for the flex column (it must never carry a pixel size).
 *  - R6: clamp any remaining sizing below MIN_COLUMN_WIDTH up to the floor.
 *  - R7: keep `spaceMode` only when it's one of the three valid values;
 *    drop anything else (defensive — normalizeColumnPrefs already validates
 *    it on load, but reconcilePrefs re-validates rather than trusting its
 *    input's static type, matching the pattern of every other field here).
 * Returns a new object; never mutates the input.
 */
export function reconcilePrefs(
  prefs: ColumnPrefs,
  columns: ColumnDef<any, any>[],
): ColumnPrefs {
  const validIds = new Set(
    columns.map(columnIdOf).filter((id): id is string => id != null),
  );

  // R1: drop unknown visibility ids
  const columnVisibility: VisibilityState = {};
  for (const [id, visible] of Object.entries(prefs.columnVisibility)) {
    if (validIds.has(id)) columnVisibility[id] = visible;
  }

  // R2: strip enableHiding:false columns from visibility
  for (const col of columns) {
    if ((col as any).enableHiding === false) {
      const id = columnIdOf(col);
      if (id != null) delete columnVisibility[id];
    }
  }

  // R3: sanitize order — existing ids only, dedupe (keep first), no injection
  const seenOrder = new Set<string>();
  const columnOrder: ColumnOrderState = [];
  for (const id of prefs.columnOrder) {
    if (validIds.has(id) && !seenOrder.has(id)) {
      columnOrder.push(id);
      seenOrder.add(id);
    }
  }

  // R4 + R5: drop sizing for unknown ids and for the flex column
  const flexIds = new Set(
    columns
      .filter((col) => isFlexColumn(col))
      .map(columnIdOf)
      .filter((id): id is string => id != null),
  );
  const columnSizing: ColumnSizingState = {};
  for (const [id, size] of Object.entries(prefs.columnSizing)) {
    if (!validIds.has(id) || flexIds.has(id)) continue;
    // R6: clamp sizing floor
    columnSizing[id] = size < MIN_COLUMN_WIDTH ? MIN_COLUMN_WIDTH : size;
  }

  // R7: keep spaceMode only when it's one of the three valid values.
  const spaceMode = coerceSpaceMode(prefs.spaceMode);

  return {
    schemaVersion: COLUMN_PREFS_VERSION,
    columnVisibility,
    columnOrder,
    columnSizing,
    ...(spaceMode ? { spaceMode } : {}),
  };
}

/**
 * The effective full column order: saved order first (existing ids only),
 * then any not-yet-ordered ids appended in definition order. Mirrors
 * TanStack's own columnOrder resolution so the UI matches the table (D14).
 */
export function deriveOrderedIds(
  allLeafIds: string[],
  columnOrder: ColumnOrderState,
): string[] {
  if (columnOrder.length === 0) return allLeafIds;

  const seen = new Set<string>();
  const ordered: string[] = [];
  for (const id of columnOrder) {
    if (allLeafIds.includes(id) && !seen.has(id)) {
      ordered.push(id);
      seen.add(id);
    }
  }
  for (const id of allLeafIds) {
    if (!seen.has(id)) {
      ordered.push(id);
      seen.add(id);
    }
  }
  return ordered;
}
